//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useCallback, useEffect, useRef } from 'react';

import { CHAT_PANEL_ID } from '@/components/Chat/chatPanelId';
import { useChatAvailability } from '@/components/Chat/useChatAvailability';
import {
  ASK_AI_STEP_ID, DATA_WAIT_STEP_ID, DEMO_STEP_INDEXES,
  GETTING_STARTED_TOUR_ID, gettingStartedStepIndex, gettingStartedTourInfo
} from '@/components/Help/tours/gettingStartedTour';
import {
  isTargetMissing
} from '@/components/Help/tours/components/stepNavigation';
import { canStartItself } from '@/components/Help/tours/tourSteps';
import { useAuth } from '@/hooks/useAuth';
import { useDataset } from '@/hooks/useDataset';
import { useHelp } from '@/hooks/useHelp';
import { usePanels } from '@/hooks/usePanels';

const DATA_WAIT_STEP = gettingStartedStepIndex(DATA_WAIT_STEP_ID);
const ASK_AI_STEP = gettingStartedStepIndex(ASK_AI_STEP_ID);

/**
 * Starts the Getting Started tour at the help moment -- when the Help icon
 * would otherwise begin to pulse, a few idle seconds after signing in -- for
 * a user whose server has no dataset to work on, and drives what the tour
 * cannot see
 * for itself: whether the server has data, whether the chat panel is open,
 * and whether chat can be used at all.
 *
 * Takes the moment at most once per sign-in, and declines it otherwise, so
 * the icon pulses as it always did. A tour the user closed does not come
 * back until the next sign-in, and one they opted out of does not come back
 * at all; the Help menu still runs it by hand.
 */
const OnboardingTourStarter = () => {
  const { isAuthenticated, userProfile } = useAuth();
  const { choosableDatasets, datasetsLoaded, demoIsLoading } = useDataset();
  const { tourState, tourActions, helpActions } = useHelp();
  const { setHelpMomentHandler } = helpActions;
  const { isPanelOpen } = usePanels();
  const { isDisabled: isChatDisabled } = useChatAvailability();
  const { activeTour, tourStep, isNextDisabled, optedOutTours } = tourState;
  const { startTour, setTourStep, setIsNextDisabled, endTour } = tourActions;

  // The profile too, not the token alone: the opt-out is stored under a key
  // derived from the profile, and before it arrives the list reads as empty.
  const isSignedIn = isAuthenticated() && userProfile !== null;
  // Data as the header's selector counts it: a dataset someone could work
  // on. A namespace holding nothing but query results is listed by the
  // server, and is not one; the selector says "No datasets" over it, and so
  // does this.
  const hasData = choosableDatasets.length > 0;
  const isLoadingDemo = Object.values(demoIsLoading).some(Boolean);
  const isChatPanelOpen = isPanelOpen(CHAT_PANEL_ID);
  const onThisTour = activeTour === GETTING_STARTED_TOUR_ID;
  const hasStarted = useRef(false);

  useEffect(() => {
    if (!isSignedIn) hasStarted.current = false;
  }, [isSignedIn]);

  // Registered again whenever a condition changes, so the handler always
  // reads the committed ones; the provider keeps only the latest handler,
  // and replacing it costs a pointer write.
  useEffect(() => {
    setHelpMomentHandler(() => {
      // Loaded, not merely not loading: a list that failed to load is also
      // empty, and says nothing about the server. A list still loading at
      // the moment is left to the pulse rather than guessed at.
      if (hasStarted.current || !isSignedIn || !datasetsLoaded || hasData)
        return false;
      // Asked the way the leave gate asks it, so the two cannot disagree
      // about whether this tour can still arrive uninvited -- the question
      // that gate's "don't show it again" exists to answer.
      if (activeTour !== null ||
          !canStartItself(gettingStartedTourInfo, optedOutTours))
        return false;
      // Nowhere to point without the first step's target: the demo pill is
      // not drawn on a mobile header, when unpinned, or when the rail has
      // folded it into the overflow menu. Asked the way the tour itself
      // asks it, so the two cannot disagree about what being there means.
      if (isTargetMissing(gettingStartedTourInfo.steps[0].target))
        return false;
      hasStarted.current = true;
      startTour(GETTING_STARTED_TOUR_ID);
      return true;
    });
    return () => setHelpMomentHandler(null);
  }, [setHelpMomentHandler, startTour, isSignedIn, datasetsLoaded, hasData,
      activeTour, optedOutTours]);

  // The demo steps. Once the server has data they are optional, so Next is
  // open on each of them whatever the step asked for: a server with both
  // demo datasets loaded grays out the whole menu and leaves nothing to
  // click. Without data, the wait step holds Next only while a load is in
  // flight; a load that ends without data may have failed, and Next opens
  // so the user is not stranded. Only a hold that actually happened moves
  // the tour on when data arrives, so a step reached with data already
  // there stays in the user's hands and Back still lands on it. The hold
  // is remembered until the data arrives or the demo steps are left, not
  // only while the load flag is up: the loader clears its flag a fixed
  // second after asking for the dataset list again, and a slow answer
  // lands after that.
  // Moving the tour on is the controller's rule as well as the starter's:
  // a step whose target is not there leaves an overlay over nothing until
  // Joyride's target wait runs out, so the tour ends instead.
  const moveTo = useCallback((index: number) => {
    if (isTargetMissing(gettingStartedTourInfo.steps[index].target)) endTour();
    else setTourStep(index);
  }, [endTour, setTourStep]);

  const onDemoSteps = onThisTour && DEMO_STEP_INDEXES.includes(tourStep);
  const shouldHold = onDemoSteps && tourStep === DATA_WAIT_STEP &&
                     !hasData && isLoadingDemo;
  const waited = useRef(false);
  useEffect(() => {
    if (!onDemoSteps) {
      waited.current = false;
      return;
    }
    if (shouldHold) {
      waited.current = true;
      // Re-asserted rather than set once: the controller enables Next as
      // every step begins, and this runs after that.
      if (!isNextDisabled) setIsNextDisabled(true);
      return;
    }
    const wasHolding = waited.current;
    if (hasData && wasHolding) {
      waited.current = false;
      moveTo(DATA_WAIT_STEP + 1);
    }
    if ((hasData || wasHolding) && isNextDisabled) setIsNextDisabled(false);
  }, [onDemoSteps, shouldHold, hasData, isNextDisabled, setIsNextDisabled,
      moveTo]);

  // Which way the tour arrived at the current step, so a step passed by on
  // the way forward is not passed by again when Back returns to it.
  const lastStep = useRef<number | null>(null);
  const arrivedByBack = useRef(false);
  useEffect(() => {
    arrivedByBack.current =
      lastStep.current !== null && tourStep < lastStep.current;
    lastStep.current = onThisTour ? tourStep : null;
  }, [onThisTour, tourStep]);

  // The Ask AI step asks for a click on the pill. With the panel already
  // open that click would park it, so the step is passed by on the way
  // forward, and on the way back Next is opened instead, so Back is not a
  // dead key. With chat unusable the pill takes no click, so Next is opened
  // and, the panel never appearing, ends the tour.
  useEffect(() => {
    if (!onThisTour || tourStep !== ASK_AI_STEP) return;
    if (isChatPanelOpen && !arrivedByBack.current) {
      moveTo(ASK_AI_STEP + 1);
      return;
    }
    if ((isChatPanelOpen || isChatDisabled) && isNextDisabled)
      setIsNextDisabled(false);
  }, [onThisTour, tourStep, isChatPanelOpen, isChatDisabled, isNextDisabled,
      setIsNextDisabled, moveTo]);

  return null;
};

export default OnboardingTourStarter;
